import { useRef, useState } from 'react';
import { useTokens } from '../../shared/theme.jsx';
import { Button, Field } from '../../shared/uiKit.jsx';
import { submitInstitutionSupportRequest } from '../../shared/services/institutions.js';

const REQUEST_TYPES = [
  { value: 'GENERAL', label: 'General help', description: 'Questions about your portal, program, or account.' },
  { value: 'BILLING', label: 'Billing change', description: 'Seats, invoices, funding, plan, or contract changes.' },
  { value: 'FEATURE', label: 'Feature request', description: 'Tell us what would make the program work better.' },
  { value: 'FEEDBACK', label: 'Share feedback', description: 'Send product or program feedback directly to the team.' },
];

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) { return `${Math.max(1, Math.round(bytes / 1024))} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// A link can preselect a request type, e.g. Billing's "Request an invoice"
// opens /support?type=BILLING. Unknown values fall back to General help.
const initialRequestType = () => {
  const requested = typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search).get('type');
  return REQUEST_TYPES.some((option) => { return option.value === requested; }) ? requested : 'GENERAL';
};

export default function PortalSupportView() {
  const t = useTokens();
  const [type, setType] = useState(initialRequestType);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const attachmentInputRef = useRef(null);

  const addAttachments = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (selected.length === 0) { return; }
    if (attachments.length + selected.length > MAX_ATTACHMENTS) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }
    const oversized = selected.find((file) => { return file.size > MAX_ATTACHMENT_SIZE; });
    if (oversized) {
      setError(`${oversized.name} is larger than 5 MB.`);
      return;
    }
    setAttachments((current) => { return [...current, ...selected]; });
    setError('');
  };

  const removeAttachment = (index) => {
    setAttachments((current) => { return current.filter((file, fileIndex) => { return fileIndex !== index; }); });
  };

  const previewAttachment = (file) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => { URL.revokeObjectURL(url); }, 60000);
  };

  const downloadAttachment = (file) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => { URL.revokeObjectURL(url); }, 1000);
  };

  const submit = async (event) => {
    event.preventDefault();
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    if (trimmedSubject.length < 4) {
      setError('Add a short subject so the team can route your request.');
      return;
    }
    if (trimmedMessage.length < 20) {
      setError('Add a little more detail so the team knows what you need.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await submitInstitutionSupportRequest({
        type,
        subject: trimmedSubject,
        message: trimmedMessage,
        attachments,
      });
      setSubmitted(result);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setType('GENERAL');
    setSubject('');
    setMessage('');
    setAttachments([]);
    setError('');
    setSubmitted(null);
  };

  if (submitted) {
    const request = submitted.request;
    const allAdminsNotified = submitted.notification?.sent > 0 && submitted.notification?.failed === 0;
    return (
      <section className="max-w-2xl">
        <div className="border-y py-8" style={{ borderColor: t.hairline }}>
          <span
            className="inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em]"
            style={{ borderColor: t.border, color: t.muted, backgroundColor: t.statBg }}
          >
            Request received
          </span>
          <h2 className="mt-4 text-[26px] leading-tight" style={{ color: t.heading, fontFamily: t.fonts.display }}>
            It’s with the Coffee Chats team.
          </h2>
          <p className="mt-2 text-base leading-relaxed" style={{ color: t.muted }}>
            {allAdminsNotified
              ? 'Every platform administrator has been notified. We’ll follow up by email.'
              : 'Your request is in the platform support inbox. We’ll follow up by email.'}
          </p>
          <div className="mt-6 rounded-xl border px-5 py-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
            <p className="text-[11px] uppercase tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
              Ticket
            </p>
            <p className="mt-1 text-lg font-semibold" style={{ color: t.heading, fontFamily: t.fonts.mono }}>
              {request.ticketId}
            </p>
            <p className="mt-2 text-sm" style={{ color: t.body }}>{request.subject}</p>
            {submitted.receiptSent ? (
              <p className="mt-2 text-xs" style={{ color: t.muted }}>A branded receipt was sent to your account email.</p>
            ) : null}
          </div>
          <div className="mt-6">
            <Button variant="secondary" label="Send another request" onClick={reset} />
          </div>
        </div>
      </section>
    );
  }

  const textareaStyle = {
    minHeight: 170,
    borderColor: t.inputBorder,
    backgroundColor: t.inputBg,
    color: t.inputText,
    fontFamily: t.fonts.body,
  };

  return (
    <form onSubmit={submit} className="max-w-3xl">
      <section>
        <h2 className="text-xl" style={{ color: t.heading, fontFamily: t.fonts.display }}>
          What can we help with?
        </h2>
        <p className="mt-1 text-sm leading-relaxed" style={{ color: t.muted }}>
          Your message goes directly to the Coffee Chats platform administrators and appears in their support inbox.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {REQUEST_TYPES.map((option) => {
            const selected = type === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() => { setType(option.value); }}
                className={`min-h-[92px] rounded-xl border p-4 text-left transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                style={{
                  borderColor: selected ? t.heading : t.border,
                  backgroundColor: selected ? t.statBg : t.surface,
                  boxShadow: selected ? `inset 3px 0 0 ${t.accent}` : 'none',
                }}
              >
                <span className="block text-[15px] font-semibold" style={{ color: t.heading }}>{option.label}</span>
                <span className="mt-1 block text-sm leading-snug" style={{ color: t.muted }}>{option.description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mt-8 border-y py-6" style={{ borderColor: t.hairline }}>
        <label className="block text-sm font-medium" style={{ color: t.body }}>
          <span className="mb-1.5 block">Subject</span>
          <Field
            value={subject}
            onChangeText={setSubject}
            placeholder={type === 'BILLING' ? 'Increase our sponsored seat allocation' : 'Briefly describe your request'}
          />
        </label>
        <label className="mt-4 block text-sm font-medium" style={{ color: t.body }}>
          <span className="mb-1.5 block">Details</span>
          <textarea
            value={message}
            onChange={(event) => { setMessage(event.target.value); }}
            maxLength={5000}
            placeholder="Include the outcome you need, any relevant dates, and who the change should affect."
            className={`block w-full resize-y rounded-md border px-3 py-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
            style={textareaStyle}
          />
        </label>
        <div className="mt-2 flex items-center justify-between gap-4">
          <span className="text-xs" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
            {`${message.length} / 5000`}
          </span>
          <span className="text-xs text-right" style={{ color: t.muted }}>
            Replies go to your account email.
          </span>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium" style={{ color: t.body }}>Attachments</p>
              <p className="mt-0.5 text-xs" style={{ color: t.muted }}>
                Up to 10 files, 5 MB each.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { attachmentInputRef.current?.click(); }}
              disabled={submitting || attachments.length >= MAX_ATTACHMENTS}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
              style={{ borderColor: t.border, color: t.heading, backgroundColor: t.surface }}
            >
              Add files
            </button>
            <input
              ref={attachmentInputRef}
              type="file"
              multiple
              aria-label="Add attachments"
              className="sr-only"
              onChange={addAttachments}
              disabled={submitting || attachments.length >= MAX_ATTACHMENTS}
            />
          </div>
          {attachments.length > 0 ? (
            <ul className="mt-3 space-y-2" aria-label="Selected attachments">
              {attachments.map((file, index) => {
                return (
                  <li
                    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                    style={{ borderColor: t.border, backgroundColor: t.statBg }}
                  >
                    <span className="min-w-0 text-sm" style={{ color: t.body }}>
                      <span className="block truncate">{file.name}</span>
                      <span className="block text-xs" style={{ color: t.muted }}>{formatFileSize(file.size)}</span>
                    </span>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      <button
                        type="button"
                        aria-label={`Preview ${file.name}`}
                        onClick={() => { previewAttachment(file); }}
                        disabled={submitting}
                        className="text-xs underline transition-opacity hover:opacity-70 disabled:opacity-50"
                        style={{ color: t.muted }}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        aria-label={`Download ${file.name}`}
                        onClick={() => { downloadAttachment(file); }}
                        disabled={submitting}
                        className="text-xs underline transition-opacity hover:opacity-70 disabled:opacity-50"
                        style={{ color: t.muted }}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => { removeAttachment(index); }}
                        disabled={submitting}
                        className="text-xs underline transition-opacity hover:opacity-70 disabled:opacity-50"
                        style={{ color: t.muted }}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        {error ? <p role="alert" className="mt-3 text-sm" style={{ color: t.danger }}>{error}</p> : null}
        <div className="mt-5">
          <Button
            type="submit"
            label={submitting ? 'Sending request…' : 'Send request'}
            disabled={submitting || !subject.trim() || !message.trim()}
          />
        </div>
      </section>
    </form>
  );
}
